<!--
  COD4 · Provvigioni — resoconto mensile di un partner.

  SORGENTE: la tabella `provvigione`. **Una sola tabella, due origini**: ogni
  riga è legata o a un `booking` o a un `minute` (pacchetto valet) — le
  provvigioni sul valet le crea `confirmCustomer()` quando il cliente accetta,
  con `importo = imponibile × partner.provvigione / 100`.
  Qui sono un elenco solo, con una colonna che dice da dove viene ciascuna
  riga.

  `type` vale `accepted` o `rejected`: le rifiutate **non sono un ricavo** e
  non vengono caricate.

  L'id della rotta è quello del **partner**.

  Si selezionano le righe, si indica un riferimento fattura e si segnano
  come liquidate, con conferma.
-->
<template>
  <div
    v-loading="loading"
    class="c4"
  >
    <div class="c4__head">
      <button
        type="button"
        class="c4__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="c4__title">
          <span class="c4__code">COD4</span> Provvigioni
        </h1>
        <p class="c4__sub">
          {{ partnerName || 'Partner' }} — provvigioni su booking e pacchetti valet
        </p>
      </div>
    </div>

    <div class="c4__filters">
      <div class="field">
        <label>Mese</label>
        <div class="period">
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(-1)"
          >
            ‹
          </button>
          <span class="period__lbl">{{ periodLabel }}</span>
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(1)"
          >
            ›
          </button>
        </div>
      </div>
      <div class="field">
        <label>Origine</label>
        <el-select
          v-model="origin"
          style="width: 150px"
        >
          <el-option
            label="Tutte"
            value=""
          />
          <el-option
            label="Booking"
            value="booking"
          />
          <el-option
            label="Valet"
            value="minute"
          />
        </el-select>
      </div>
      <div class="c4__sp" />
      <el-input
        v-model="search"
        placeholder="Cliente o riferimento…"
        clearable
        style="width: 230px"
      />
    </div>

    <div class="kpis">
      <div class="kpi">
        <div class="kpi__lbl">
          Provvigioni del mese
        </div>
        <div class="kpi__num">
          {{ euro(kpi.total) }}
        </div>
      </div>
      <div class="kpi kpi--attn">
        <div class="kpi__lbl">
          Da liquidare
        </div>
        <div class="kpi__num">
          {{ euro(kpi.toPay) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Liquidato
        </div>
        <div class="kpi__num">
          {{ euro(kpi.paid) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Righe
        </div>
        <div class="kpi__num">
          {{ provvigioni.length }}
        </div>
      </div>
    </div>

    <div class="board">
      <div class="toolbar">
        <div class="segs">
          <button
            v-for="s in SEGMENTS"
            :key="s.key"
            type="button"
            class="seg"
            :class="{ 'is-active': segment === s.key }"
            @click="setSegment(s.key)"
          >
            {{ s.label }}
            <span class="seg__c">{{ countOf(s.key) }}</span>
          </button>
        </div>
      </div>

      <el-table
        ref="tableRef"
        :data="filtered"
        style="width: 100%"
        row-key="id"
        :empty-text="segment === 'daliq' && countOf('liq')
          ? `Niente da liquidare: ${countOf('liq')} ${countOf('liq') === 1 ? 'provvigione già liquidata' : 'provvigioni già liquidate'}, nella scheda Liquidate`
          : 'Nessuna provvigione per il periodo selezionato'"
        @selection-change="onSelectionChange"
      >
        <el-table-column
          v-if="segment === 'daliq'"
          type="selection"
          width="46"
        />

        <el-table-column
          label="Data"
          width="120"
        >
          <template #default="scope">
            {{ fmtDate(scope.row.data) }}
          </template>
        </el-table-column>

        <el-table-column
          label="Origine"
          width="130"
        >
          <template #default="scope">
            <span
              class="orig"
              :class="`orig--${originOf(scope.row)}`"
            >{{ originOf(scope.row) === 'minute' ? 'Valet' : 'Booking' }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Riferimento"
          min-width="220"
        >
          <template #default="scope">
            <div class="ref__nm">
              {{ subjectOf(scope.row) }}
            </div>
            <button
              v-if="scope.row.booking?.id"
              type="button"
              class="lnk"
              @click="openBooking(scope.row.booking.id)"
            >
              {{ shortId(scope.row.booking.id) }}
            </button>
            <span
              v-else-if="scope.row.minute?.car?.plate"
              class="ref__sub"
            >{{ scope.row.minute.car.plate }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Rif. fattura"
          width="150"
        >
          <template #default="scope">
            <span class="num">{{ scope.row.rif_fattura || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Stato"
          width="150"
        >
          <template #default="scope">
            <StatusPill
              :kind="scope.row.liquidato ? 'ok' : 'attn'"
              :label="scope.row.liquidato ? 'Liquidata' : 'Da liquidare'"
            />
          </template>
        </el-table-column>

        <el-table-column
          label="Importo"
          width="140"
          align="right"
        >
          <template #default="scope">
            <span class="num val">{{ euro(scope.row.importo) }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div
        v-if="segment === 'daliq'"
        class="bulk"
      >
        <span class="bulk__info">
          <b>{{ selected.length }}</b> selezionate · totale
          <b>{{ euro(selectedTotal) }}</b>
        </span>
        <span class="c4__sp" />
        <el-input
          v-model="rifFattura"
          placeholder="Rif. fattura"
          style="width: 170px"
        />
        <el-button
          type="success"
          :disabled="!selected.length"
          :loading="liquidating"
          @click="confirmLiquidate"
        >
          Segna liquidate
        </el-button>
      </div>

      <div class="board__foot">
        <span>{{ filtered.length }} provvigioni</span>
        <span class="c4__sp" />
        <span class="board__tot">
          Totale mostrato <b>{{ euro(filteredTotal) }}</b>
        </span>
      </div>
    </div>

    <p class="c4__note">
      Sono elencate solo le provvigioni <b>accettate</b>: le rifiutate non sono
      un ricavo. Quelle sul valet nascono quando il cliente conferma il
      pacchetto, in percentuale sul prezzo Simba del pacchetto.
    </p>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import contabilitaAPI from '../../../services/contabilitaAPI.js';
import { periodFromRoute, syncPeriodToUrl } from '../../../services/period.js';
import partnerAPI from '../../../services/partnerAPI.js';
import StatusPill from '../../../components/ui/StatusPill.vue';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const SEGMENTS = [
  { key: 'daliq', label: 'Da liquidare' },
  { key: 'liq', label: 'Liquidate' },
];

export default {
  name: 'Cod4Page',
  components: { StatusPill },

  setup() {
    const route = useRoute();
    const router = useRouter();
    // Il periodo arriva dalla URL (?y=&m=), non dal calendario: chi entra
    // dall'hub deve restare sul mese che stava guardando. Senza query valida
    // si ricade sul mese corrente.
    const periodo = periodFromRoute(route);
    const year = ref(periodo.year);
    const month = ref(periodo.month);

    const provvigioni = ref([]);
    const loading = ref(false);
    const liquidating = ref(false);
    const search = ref('');
    const origin = ref('');
    const segment = ref('daliq');
    const selected = ref([]);
    const rifFattura = ref('');
    const partnerName = ref('');
    const tableRef = ref(null);

    const periodLabel = computed(() => `${MONTHS[month.value - 1]} ${year.value}`);

    // La riga è legata a un booking OPPURE a un minute: da lì l'origine.
    const originOf = (p) => (p.minute ? 'minute' : 'booking');

    const subjectOf = (p) => {
      if (p.minute) {
        const c = p.minute.car;
        return [c?.brand, c?.model].filter(Boolean).join(' ') || 'Pacchetto valet';
      }
      const cu = p.booking?.customer;
      return [cu?.name, cu?.surname].filter(Boolean).join(' ') || 'Booking';
    };

    const bySegment = (key) =>
      provvigioni.value.filter((p) => (key === 'liq' ? p.liquidato : !p.liquidato));

    const countOf = (key) => bySegment(key).length;

    const filtered = computed(() => {
      const q = search.value.trim().toLowerCase();
      return bySegment(segment.value).filter((p) => {
        if (origin.value && originOf(p) !== origin.value) return false;
        if (!q) return true;
        return `${subjectOf(p)} ${p.rif_fattura || ''}`.toLowerCase().includes(q);
      });
    });

    const filteredTotal = computed(() =>
      filtered.value.reduce((s, p) => s + Number(p.importo || 0), 0)
    );

    const selectedTotal = computed(() =>
      selected.value.reduce((s, p) => s + Number(p.importo || 0), 0)
    );

    const kpi = computed(() => {
      let total = 0;
      let paid = 0;
      let toPay = 0;
      for (const p of provvigioni.value) {
        const v = Number(p.importo || 0);
        total += v;
        if (p.liquidato) paid += v;
        else toPay += v;
      }
      return { total, paid, toPay };
    });

    const load = async () => {
      loading.value = true;
      selected.value = [];
      try {
        const res = await contabilitaAPI.cod4Rows(
          route.params.id, year.value, month.value
        );
        provvigioni.value = res.provvigioni;
      } catch (e) {
        console.error('Caricamento COD4 fallito:', e);
        provvigioni.value = [];
      } finally {
        loading.value = false;
      }
    };

    const loadPartner = async () => {
      try {
        const r = await partnerAPI.getPartnerDetail(route.params.id);
        partnerName.value = r.data?.company_name || '';
      } catch (e) {
        console.warn('Nome partner non disponibile:', e?.message);
      }
    };

    // Agisce sulla selezione e dice cosa farà prima di procedere.
    const confirmLiquidate = async () => {
      if (!rifFattura.value.trim()) {
        ElMessage.warning('Indica il riferimento fattura');
        return;
      }
      try {
        await ElMessageBox.confirm(
          `${selected.value.length} provvigioni per ${euro(selectedTotal.value)} ` +
            `verranno segnate come liquidate con il riferimento "${rifFattura.value}".`,
          'Confermi la liquidazione?',
          { confirmButtonText: 'Segna liquidate', cancelButtonText: 'Annulla' }
        );
      } catch {
        return;
      }

      liquidating.value = true;
      const res = await Promise.allSettled(
        selected.value.map((p) =>
          contabilitaAPI.updateFatturaProvvigione(p.id, {
            rif_fattura: rifFattura.value,
            liquidato: true,
          })
        )
      );
      liquidating.value = false;

      const ko = res.filter((r) => r.status === 'rejected').length;
      if (ko) {
        ElMessage.error(
          `Liquidate ${res.length - ko} provvigioni su ${res.length}: ${ko} non riuscite.`
        );
      } else {
        ElMessage.success(`${res.length} provvigioni segnate come liquidate`);
        rifFattura.value = '';
      }
      await load();
    };

    const setSegment = (key) => {
      segment.value = key;
      selected.value = [];
      tableRef.value?.clearSelection?.();
    };

    const onSelectionChange = (rows) => { selected.value = rows; };

    const shiftMonth = (delta) => {
      let m = month.value + delta;
      let y = year.value;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      month.value = m;
      year.value = y;
      // la URL segue il mese mostrato: un refresh non riporta a oggi
      syncPeriodToUrl(router, route, y, m);
      load();
    };

    function euro(n) {
      return `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
    }

    const fmtDate = (d) =>
      d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : '—';

    const shortId = (id) => `#${String(id).slice(0, 8)}`;

    const openBooking = (id) => router.push(`/booking/detail/${id}`);
    const goBack = () => router.push('/contabilita');

    onMounted(() => { load(); loadPartner(); });

    return {
      year, month, provvigioni, loading, liquidating, search, origin, segment,
      selected, rifFattura, partnerName, tableRef,
      filtered, filteredTotal, selectedTotal, kpi, periodLabel, SEGMENTS,
      countOf, setSegment, onSelectionChange, confirmLiquidate, shiftMonth,
      euro, fmtDate, shortId, originOf, subjectOf, openBooking, goBack,
    };
  },
};
</script>

<style scoped>
.c4 { padding: 4px 0 32px; }
.c4__sp { flex: 1; }

.c4__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 18px;
}
.c4__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.c4__back:hover { border-color: var(--brand-violet); }
.c4__title {
  margin: 0; font-size: 21px; display: flex; align-items: center; gap: 10px;
  color: var(--text-primary);
}
.c4__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; font-weight: 700; color: #fff; background: var(--state-ok);
  padding: 3px 8px; border-radius: 6px;
}
.c4__sub { margin: 3px 0 0; font-size: 13px; color: var(--text-tertiary); }

.c4__filters {
  display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 14px;
}
.field {
  display: flex; align-items: center; gap: 8px; padding: 5px 11px;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: 10px;
}
.field label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700;
}
.period { display: flex; align-items: center; gap: 2px; }
.period__nav {
  width: 30px; height: 30px; border: 0; background: transparent;
  border-radius: 7px; cursor: pointer; font-size: 15px;
  color: var(--text-secondary); font-family: inherit;
}
.period__nav:hover { background: var(--bg-tertiary); }
.period__lbl {
  padding: 0 8px; font-weight: 700; font-variant-numeric: tabular-nums;
  min-width: 118px; text-align: center; color: var(--text-primary);
}

.kpis {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;
}
@media (max-width: 820px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
.kpi {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); padding: 13px 15px;
}
.kpi__lbl {
  font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 600;
}
.kpi__num {
  font-size: 22px; font-weight: 750; letter-spacing: -0.02em; margin-top: 4px;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.kpi--attn .kpi__num { color: var(--state-attn); }

.board {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.toolbar {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-bottom: 1px solid var(--border-color);
}
.segs { display: flex; gap: 5px; }
.seg {
  border: 1px solid transparent; background: transparent; cursor: pointer;
  color: var(--text-secondary); padding: 7px 12px; border-radius: 8px;
  font-size: 13px; font-weight: 600; font-family: inherit;
  display: inline-flex; align-items: center; gap: 8px;
}
.seg:hover { background: var(--bg-tertiary); }
.seg.is-active {
  background: var(--svc-fleet-bg); color: var(--brand-violet);
  border-color: var(--brand-violet);
}
.seg__c {
  font-size: 11.5px; font-weight: 700; border-radius: 20px; padding: 1px 8px;
  background: var(--state-muted-bg); color: var(--state-muted);
}
.seg.is-active .seg__c { background: var(--brand-violet); color: #fff; }

.bulk {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 16px; border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}
.bulk__info { font-size: 12.5px; color: var(--text-secondary); }
.bulk__info b { font-variant-numeric: tabular-nums; color: var(--text-primary); }

.orig {
  font-size: 11px; font-weight: 700; border-radius: 20px; padding: 2px 9px;
}
.orig--booking { color: var(--svc-pickup-ink); background: var(--svc-pickup-bg); }
.orig--minute { color: var(--svc-fleet); background: var(--svc-fleet-bg); }

.ref__nm { font-weight: 600; color: var(--text-primary); }
.ref__sub { color: var(--text-tertiary); font-size: 11.5px; }
.num { font-variant-numeric: tabular-nums; }
.val { font-weight: 700; color: var(--text-primary); }
.lnk {
  background: none; border: 0; padding: 0; cursor: pointer; font-family: inherit;
  color: var(--brand-violet); font-weight: 600; font-size: 12px;
}
.lnk:hover { text-decoration: underline; }

.board__foot {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-top: 1px solid var(--border-color);
  font-size: 12.5px; color: var(--text-tertiary);
}
.board__tot { color: var(--text-secondary); }
.board__tot b {
  color: var(--text-primary); font-variant-numeric: tabular-nums; font-size: 14px;
}

.c4__note {
  margin: 12px 0 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5;
}

.c4 :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 10px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
